/**
 * Crisp vector playing cards (no image assets). Accessible: each card announces "Ace of spades" etc.
 * Four-colour mode (♣ green, ♦ blue) helps colour-blind players; suit shapes always differ too.
 */
import { memo } from 'react';
import { parseCard } from '@cardhub/engine';
import type { CardBackId } from '@cardhub/protocol';
import { useSettings } from '../store/settings';

const SUIT_SYMBOL = { S: '♠', H: '♥', D: '♦', C: '♣' } as const;
const SUIT_NAME = { S: 'spades', H: 'hearts', D: 'diamonds', C: 'clubs' } as const;
const RANK_LABEL: Record<number, string> = { 11: 'J', 12: 'Q', 13: 'K', 14: 'A', 10: '10' };
const RANK_NAME: Record<number, string> = { 11: 'Jack', 12: 'Queen', 13: 'King', 14: 'Ace' };

export function cardLabel(id: string): string {
  const c = parseCard(id);
  if (c.joker) return 'Joker';
  return `${RANK_NAME[c.rank] ?? c.rank} of ${SUIT_NAME[c.suit]}`;
}

export function shortCard(id: string): string {
  const c = parseCard(id);
  if (c.joker) return '🃏';
  return `${RANK_LABEL[c.rank] ?? c.rank}${SUIT_SYMBOL[c.suit]}`;
}

function suitColor(suit: 'S' | 'H' | 'D' | 'C', fourColor: boolean): string {
  if (fourColor) return { S: 'var(--card-black)', H: 'var(--card-red)', D: 'var(--card-blue)', C: 'var(--card-green)' }[suit];
  return suit === 'H' || suit === 'D' ? 'var(--card-red)' : 'var(--card-black)';
}

export interface CardProps {
  id?: string | null;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  faceDown?: boolean;
  selected?: boolean;
  playable?: boolean;
  unplayable?: boolean;
  wild?: boolean;
  dealIndex?: number;
  onClick?: () => void;
  style?: React.CSSProperties;
  className?: string;
  /** Style the face to match this back (shop previews); defaults to the player's chosen back. */
  theme?: CardBackId;
}

/** Card-face styling that matches each card back: tinted paper, framed border, and a faint watermark of the back's motif. */
const FACES: Record<CardBackId, { p1: string; p2: string; edge: string; accent: string }> = {
  classic: { p1: '', p2: '', edge: 'rgba(0,0,0,0.18)', accent: '#2dd4bf' },
  crimson: { p1: '#fff7f5', p2: '#ffe1e3', edge: '#9f1239', accent: '#d4a017' },
  midnight: { p1: '#f3f5ff', p2: '#d9e0fb', edge: '#1e2a5a', accent: '#d4a017' },
  saffron: { p1: '#fffaf0', p2: '#ffe9c7', edge: '#c2410c', accent: '#ea8a1c' },
  himalaya: { p1: '#f5fbff', p2: '#d3e8fb', edge: '#1e3a8a', accent: '#3b82f6' },
  gold: { p1: '#fffdf0', p2: '#f8e08a', edge: '#a16207', accent: '#713f12' },
};

function FaceMark({ theme, color }: { theme: CardBackId; color: string }) {
  switch (theme) {
    case 'crimson':
      return <path d={HEART} transform="translate(50 72) scale(3.2)" fill={color} />;
    case 'midnight':
      return (
        <g fill={color}>
          <circle cx="50" cy="72" r="26" />
          <circle cx="60" cy="64" r="22" fill="var(--mark-cut, #e6ebfb)" />
        </g>
      );
    case 'saffron':
      return (
        <g transform="translate(50 72)" fill={color}>
          {Array.from({ length: 12 }, (_, i) => (
            <ellipse key={i} cx="0" cy="-20" rx="6" ry="14" transform={`rotate(${i * 30})`} />
          ))}
        </g>
      );
    case 'himalaya':
      return <path d="M14 108 L38 62 L52 84 L68 46 L90 108Z" fill={color} />;
    case 'gold':
      return <path d={CROWN} transform="translate(50 74) scale(2.2)" fill={color} />;
    default:
      return <path d="M50 36 L78 72 L50 108 L22 72Z" fill={color} />;
  }
}

export const PlayingCard = memo(function PlayingCard({ id, size = 'md', faceDown, selected, playable, unplayable, wild, dealIndex, onClick, style, className = '', theme }: CardProps) {
  const fourColor = useSettings((s) => s.settings.colorBlindCards);
  const chosenBack = useSettings((s) => s.settings.cardBack);
  const look = theme ?? chosenBack ?? 'classic';
  const f = FACES[look] ?? FACES.classic;
  const cls = ['pcard', size, selected && 'selected', playable && 'playable', unplayable && 'unplayable', wild && 'wild', dealIndex !== undefined && 'deal-in', className].filter(Boolean).join(' ');
  const animStyle = dealIndex !== undefined ? { animationDelay: `${dealIndex * 35}ms` } : undefined;
  if (faceDown || !id) {
    return (
      <span className={cls} style={{ ...animStyle, ...style }} aria-label="Face-down card" role="img">
        <CardBack />
      </span>
    );
  }
  const c = parseCard(id);
  const label = cardLabel(id);
  const color = c.joker ? 'var(--accent-2)' : suitColor(c.suit, fourColor);
  const rank = c.joker ? 'JK' : (RANK_LABEL[c.rank] ?? String(c.rank));
  const sym = c.joker ? '★' : SUIT_SYMBOL[c.suit];
  const face = c.rank >= 11 && c.rank <= 13 && !c.joker;
  const content = (
    <svg viewBox="0 0 100 140" aria-hidden>
      {look !== 'classic' && (
        <defs>
          <linearGradient id={`cf-${look}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={f.p1} />
            <stop offset="1" stopColor={f.p2} />
          </linearGradient>
        </defs>
      )}
      <rect x="1" y="1" width="98" height="138" rx="10" fill={look === 'classic' ? 'var(--card-face)' : `url(#cf-${look})`} stroke={f.edge} strokeWidth={look === 'classic' ? 1.5 : 2.2} />
      {look !== 'classic' && (
        <>
          <rect x="5" y="5" width="90" height="130" rx="7" fill="none" stroke={f.accent} strokeWidth="1" opacity=".75" />
          <g opacity=".13">
            <FaceMark theme={look} color={f.edge} />
          </g>
          {[[12, 131], [88, 131]].map(([x, y]) => (
            <path key={x} d={`M${x} ${y! - 3} L${x! + 2.5} ${y} L${x} ${y! + 3} L${x! - 2.5} ${y}Z`} fill={f.accent} />
          ))}
        </>
      )}
      <g fill={color} fontFamily="Inter, system-ui, sans-serif" fontWeight="800">
        <text x="9" y="27" fontSize={rank.length > 1 ? 22 : 26} textAnchor="start">
          {rank}
        </text>
        <text x="10" y="49" fontSize="20">
          {sym}
        </text>
        <g transform="rotate(180 50 70)">
          <text x="9" y="27" fontSize={rank.length > 1 ? 22 : 26}>
            {rank}
          </text>
          <text x="10" y="49" fontSize="20">
            {sym}
          </text>
        </g>
        {face ? (
          <>
            <rect x="26" y="34" width="48" height="72" rx="6" fill={color} opacity="0.1" stroke={look === 'classic' ? undefined : f.accent} strokeWidth="1.2" strokeOpacity=".8" />
            <text x="50" y="84" fontSize="40" textAnchor="middle">
              {rank}
            </text>
            <text x="50" y="103" fontSize="18" textAnchor="middle">
              {sym}
            </text>
          </>
        ) : (
          <text x="50" y="88" fontSize={c.rank === 14 ? 54 : 44} textAnchor="middle">
            {sym}
          </text>
        )}
      </g>
    </svg>
  );
  if (onClick) {
    return (
      <button type="button" className={cls} style={{ ...animStyle, ...style }} onClick={onClick} aria-label={label} aria-pressed={selected} disabled={unplayable && !selected}>
        {content}
      </button>
    );
  }
  return (
    <span className={cls} style={{ ...animStyle, ...style }} role="img" aria-label={label}>
      {content}
    </span>
  );
});

const BACKS: Record<CardBackId, { from: string; to: string; ink: string; mark: string }> = {
  classic: { from: '#3b2bb5', to: '#0f766e', ink: '#fff', mark: 'T' },
  crimson: { from: '#be123c', to: '#4c0519', ink: '#fde68a', mark: '♥' },
  midnight: { from: '#1e2a5a', to: '#020617', ink: '#facc15', mark: '★' },
  saffron: { from: '#f59e0b', to: '#c2410c', ink: '#fff7ed', mark: '☀' },
  himalaya: { from: '#38bdf8', to: '#1e3a8a', ink: '#f0f9ff', mark: '▲' },
  gold: { from: '#fde047', to: '#a16207', ink: '#422006', mark: '♛' },
};

const STARS: [number, number, number][] = [[18, 22, 1.1], [80, 18, 0.9], [30, 40, 0.8], [70, 36, 1.2], [12, 62, 0.8], [88, 58, 0.9], [24, 96, 1], [76, 104, 0.8], [50, 118, 1.1], [14, 124, 0.8], [86, 124, 1], [40, 24, 0.7], [62, 120, 0.7]];
const HEART = 'M0 9 C-16 -3 -11 -17 0 -8 C11 -17 16 -3 0 9Z';
const CROWN = 'M-17 9 L-19 -9 L-9 0 L0 -13 L9 0 L19 -9 L17 9 Z';

/** Ornate, unique art per back. Everything is drawn in a 100x140 box so it scales crisply at any size. */
function BackArt({ id }: { id: CardBackId }) {
  switch (id) {
    case 'crimson':
      return (
        <>
          <rect x="6" y="6" width="88" height="128" rx="7" fill="url(#cb-crimson)" />
          <rect x="6" y="6" width="88" height="128" rx="7" fill="url(#p-crimson)" />
          <rect x="11" y="11" width="78" height="118" rx="5" fill="none" stroke="#fbbf24" strokeWidth="1.4" />
          <rect x="15" y="15" width="70" height="110" rx="4" fill="none" stroke="#fbbf24" strokeOpacity=".5" strokeWidth=".8" />
          <ellipse cx="50" cy="70" rx="26" ry="34" fill="#4c0519" stroke="#fbbf24" strokeWidth="2" />
          <ellipse cx="50" cy="70" rx="21" ry="29" fill="none" stroke="#fbbf24" strokeOpacity=".6" strokeWidth=".8" />
          <g transform="translate(50 70) scale(1.7)">
            <path d={HEART} fill="#fbbf24" />
            <path d={HEART} fill="none" stroke="#fff7d6" strokeWidth=".6" transform="scale(.7)" />
          </g>
          {[[22, 22], [78, 22], [22, 118], [78, 118]].map(([x, y]) => (
            <g key={`${x}${y}`} transform={`translate(${x} ${y}) scale(.7)`}>
              <path d={HEART} fill="#fbbf24" />
            </g>
          ))}
        </>
      );
    case 'midnight':
      return (
        <>
          <rect x="6" y="6" width="88" height="128" rx="7" fill="url(#cb-midnight)" />
          {STARS.map(([x, y, r], i) => (
            <circle key={i} cx={x} cy={y} r={r} fill="#fef3c7" opacity=".9" />
          ))}
          <circle cx="50" cy="70" r="30" fill="#facc15" opacity=".12" />
          <circle cx="50" cy="70" r="22" fill="#facc15" />
          <circle cx="58" cy="64" r="19" fill="#0b1226" />
          <path d="M50 14 L52 20 L58 21 L53 25 L55 31 L50 27 L45 31 L47 25 L42 21 L48 20Z" fill="#facc15" />
          <path d="M50 126 L52 120 L58 119 L53 115 L55 109 L50 113 L45 109 L47 115 L42 119 L48 120Z" fill="#facc15" />
          <rect x="10" y="10" width="80" height="120" rx="5" fill="none" stroke="#facc15" strokeWidth="1.2" />
          <rect x="14" y="14" width="72" height="112" rx="3" fill="none" stroke="#facc15" strokeOpacity=".4" strokeWidth=".7" strokeDasharray="2 3" />
        </>
      );
    case 'saffron':
      return (
        <>
          <rect x="6" y="6" width="88" height="128" rx="7" fill="url(#cb-saffron)" />
          <rect x="6" y="6" width="88" height="128" rx="7" fill="url(#p-saffron)" />
          <g transform="translate(50 70)">
            {Array.from({ length: 16 }, (_, i) => (
              <ellipse key={`a${i}`} cx="0" cy="-30" rx="5" ry="13" fill="#fff7ed" opacity=".25" transform={`rotate(${i * 22.5})`} />
            ))}
            {Array.from({ length: 12 }, (_, i) => (
              <ellipse key={`b${i}`} cx="0" cy="-21" rx="5.5" ry="11" fill="#7c2d12" stroke="#fde68a" strokeWidth=".9" transform={`rotate(${i * 30})`} />
            ))}
            {Array.from({ length: 8 }, (_, i) => (
              <ellipse key={`c${i}`} cx="0" cy="-11" rx="4" ry="8" fill="#fde68a" transform={`rotate(${i * 45 + 22.5})`} />
            ))}
            <circle r="6.5" fill="#7c2d12" stroke="#fff7ed" strokeWidth="1.4" />
            <circle r="2.4" fill="#fde68a" />
          </g>
          <rect x="11" y="11" width="78" height="118" rx="5" fill="none" stroke="#fff7ed" strokeWidth="1.3" />
          <rect x="14.5" y="14.5" width="71" height="111" rx="3.5" fill="none" stroke="#fff7ed" strokeOpacity=".5" strokeWidth=".7" strokeDasharray="1 3" />
        </>
      );
    case 'himalaya':
      return (
        <>
          <rect x="6" y="6" width="88" height="128" rx="7" fill="url(#cb-himalaya)" />
          {STARS.slice(0, 8).map(([x, y, r], i) => (
            <circle key={i} cx={x} cy={Math.min(y, 56)} r={r * 0.8} fill="#fff" opacity=".85" />
          ))}
          <circle cx="68" cy="52" r="15" fill="#fef3c7" opacity=".18" />
          <circle cx="68" cy="52" r="9" fill="#fef3c7" opacity=".95" />
          <path d="M6 112 L30 66 L44 88 L62 48 L94 112 L94 128 Q94 134 88 134 L12 134 Q6 134 6 128Z" fill="#1e3a8a" />
          <path d="M62 48 L52 68 L58 64 L62 72 L67 63 L74 70Z" fill="#f8fafc" />
          <path d="M30 66 L23 79 L28 76 L31 82 L35 75 L38 78Z" fill="#e0f2fe" />
          <path d="M6 120 L26 94 L42 112 L58 90 L94 124 L94 128 Q94 134 88 134 L12 134 Q6 134 6 128Z" fill="#0b2a6b" opacity=".92" />
          <rect x="10" y="10" width="80" height="120" rx="5" fill="none" stroke="#f0f9ff" strokeWidth="1.3" />
        </>
      );
    case 'gold':
      return (
        <>
          <rect x="6" y="6" width="88" height="128" rx="7" fill="url(#cb-gold)" />
          <rect x="6" y="6" width="88" height="128" rx="7" fill="url(#p-gold)" />
          <rect x="10" y="10" width="80" height="120" rx="5" fill="none" stroke="#713f12" strokeWidth="2" />
          <rect x="14" y="14" width="72" height="112" rx="3" fill="none" stroke="#fffbeb" strokeOpacity=".9" strokeWidth=".9" />
          <circle cx="50" cy="70" r="27" fill="#713f12" stroke="#fffbeb" strokeWidth="2" />
          <circle cx="50" cy="70" r="22" fill="none" stroke="#fde047" strokeWidth="1" strokeDasharray="1.5 2.5" />
          <g transform="translate(50 71) scale(1.15)">
            <path d={CROWN} fill="#fde047" stroke="#fffbeb" strokeWidth=".8" />
            <rect x="-17" y="9" width="34" height="4" rx="1.5" fill="#fde047" stroke="#fffbeb" strokeWidth=".6" />
            <circle cx="0" cy="-13" r="2.2" fill="#ef4444" />
            <circle cx="-19" cy="-9" r="1.8" fill="#38bdf8" />
            <circle cx="19" cy="-9" r="1.8" fill="#38bdf8" />
          </g>
          {[[20, 22], [80, 22], [20, 118], [80, 118]].map(([x, y]) => (
            <path key={`${x}${y}`} d={`M${x} ${y! - 5} L${x! + 3} ${y} L${x} ${y! + 5} L${x! - 3} ${y}Z`} fill="#713f12" />
          ))}
          <rect x="-40" y="0" width="30" height="140" fill="url(#sh-gold)" transform="skewX(-20)">
            <animate attributeName="x" values="-40;130;130" keyTimes="0;0.5;1" dur="3.6s" repeatCount="indefinite" />
          </rect>
        </>
      );
    default:
      return (
        <>
          <rect x="6" y="6" width="88" height="128" rx="7" fill="url(#cb-classic)" />
          <rect x="6" y="6" width="88" height="128" rx="7" fill="url(#p-classic)" />
          <rect x="10" y="10" width="80" height="120" rx="5" fill="none" stroke="#fff" strokeOpacity=".7" strokeWidth="1.2" />
          <path d="M50 38 L74 70 L50 102 L26 70Z" fill="#0f172a" fillOpacity=".55" stroke="#fff" strokeWidth="1.6" />
          <path d="M50 48 L66 70 L50 92 L34 70Z" fill="none" stroke="#5eead4" strokeWidth="1" />
          <text x="50" y="78" fontSize="22" fontWeight="900" textAnchor="middle" fill="#fff">
            T
          </text>
        </>
      );
  }
}

export function CardBack({ variant }: { variant?: CardBackId }) {
  const chosen = useSettings((s) => s.settings.cardBack);
  const id = variant ?? chosen ?? 'classic';
  const b = BACKS[id] ?? BACKS.classic;
  return (
    <svg viewBox="0 0 100 140" aria-hidden>
      <defs>
        <linearGradient id={`cb-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={b.from} />
          <stop offset="1" stopColor={b.to} />
        </linearGradient>
        <pattern id={`p-${id}`} width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <path d="M0 6h12M6 0v12" stroke={id === 'gold' ? 'rgba(113,63,18,0.28)' : 'rgba(255,255,255,0.16)'} strokeWidth="1" />
          <circle cx="6" cy="6" r="1.2" fill={id === 'crimson' ? 'rgba(251,191,36,.35)' : 'rgba(255,255,255,.18)'} />
        </pattern>
        <linearGradient id="sh-gold" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset=".5" stopColor="#fff" stopOpacity=".75" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <clipPath id={`clip-${id}`}>
          <rect x="1" y="1" width="98" height="138" rx="10" />
        </clipPath>
      </defs>
      <rect x="1" y="1" width="98" height="138" rx="10" fill="#fff" />
      <g clipPath={`url(#clip-${id})`}>
        <BackArt id={id} />
      </g>
      <rect x="1" y="1" width="98" height="138" rx="10" fill="none" stroke="#fff" strokeWidth="3" />
    </svg>
  );
}

/** Decorative fanned cards used on lobby game tiles. */
export function GameArt({ cards }: { cards: string[] }) {
  return (
    <div className="art" style={{ display: 'flex' }} aria-hidden>
      {cards.map((c, i) => (
        <span key={c} style={{ transform: `rotate(${(i - (cards.length - 1) / 2) * 12}deg) translateY(${Math.abs(i - (cards.length - 1) / 2) * 6}px)`, marginLeft: i ? -26 : 0 }}>
          <PlayingCard id={c} size="md" />
        </span>
      ))}
    </div>
  );
}
